<template>
  <ThePage :title="i18n(translations.title)">
    <template #pageTitle>
      {{ i18n(translations.title) }}
    </template>

    <template #pageContent>
      <RevCard class="p-24">
        <RevForm
          :initial-values="initialFormValues"
          name="create-label"
          :validate="validateForm()"
          @submit="handleSubmit"
        >
          <template #default="{ values, errors, hasError }">
            <div class="mb-28">
              <div class="heading-3 mb-12">
                {{ i18n(translations.selectOrder) }}
              </div>

              <p class="body-2 mb-12 mt-4">
                {{ i18n(translations.orderAutoFillInformation) }}
              </p>

              <div class="flex items-center">
                <RevInputText
                  id="order"
                  v-model="values.order"
                  class="flex-1"
                  data-qa="order"
                  :error="errors.order"
                  :label="i18n(translations.orderId)"
                  name="order"
                />

                <RevButtonIcon
                  :aria-label="i18n(translations.selectOrder)"
                  class="ml-24"
                  :disabled="!values.order"
                  :icon="IconSearch"
                  :loading="isFetchingOrder"
                  variant="primary"
                  @click="handleSearchOrder(values.order)"
                />
              </div>
            </div>

            <div v-if="!isLoading">
              <div class="heading-3 mb-12">
                {{ i18n(translations.shippingDetails) }}
              </div>

              <RevInputSelect
                id="shipper"
                v-model="values.shipper"
                class="mb-12"
                :error="errors.shipper"
                :label="i18n(translations.shipper)"
                name="shipper"
                :options="shippers"
              />

              <div v-if="!isFetchingOrder">
                <DynamicAddressFieldValidatorsErrorLogger
                  :errors
                  :form-values="values"
                  logger-scope="CREATE_SHIPPING_LABEL"
                />

                <AddressFields
                  v-model:city="values.city"
                  v-model:company="values.company"
                  v-model:country="values.country"
                  v-model:default-country="values.defaultCountry"
                  v-model:firstName="values.firstName"
                  v-model:lastName="values.lastName"
                  v-model:phone="values.phone"
                  v-model:postal-code="values.postalCode"
                  v-model:state-or-province="values.stateOrProvince"
                  v-model:street="values.street"
                  v-model:street2="values.street2"
                  :company-field-label-message="i18n(translations.companyLabel)"
                  :errors
                  :feature-code="FeatureCode.WEB_SHIPPING_LABEL_CREATION"
                  :optional-fields="[]"
                />
              </div>
            </div>

            <RevLoadingScreen
              v-if="isLoading || isFetchingOrder"
              class="my-32"
              :text="i18n(translations.loadingForm)"
            />

            <RevButton
              class="mt-12"
              :disabled="hasError"
              full-width="always"
              :loading="isSubmitting"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.buttonSubmit) }}
            </RevButton>
          </template>
        </RevForm>
      </RevCard>
    </template>
  </ThePage>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { ref } from 'vue'

import { getMerchantOrdersDetails } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import type { GetMerchantOrdersDetails } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-pages.types'
import { getBackshipShippers } from '@backmarket/http-api/src/api-specs-shipping/backship/shippers/shippers'
import { Country } from '@backmarket/http-api/src/standards'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import DynamicAddressFieldValidatorsErrorLogger from '@backmarket/nuxt-module-address/DynamicAddressFieldValidatorsErrorLogger.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { $httpFetch as $httpFetchV1 } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch as useHttpFetchV1 } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { decamelizeKeys } from '@backmarket/utils/object/decamelizeKeys'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconSearch } from '@ds/icons/IconSearch'

import type { PostLabelsRequest } from '~/api/shipping/labels.types'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { DEFAULT_BACKSHIP_SHIPPER } from './CreateLabel.constants'
import translations from './CreateLabel.translations'
import type { CreateLabelFormValues } from './CreateLabel.types'

const $httpFetch = useHttpFetch()
const { openErrorToast, openSuccessToast } = useTheToast()
const i18n = useI18n()
const { isFeatureEnabled } = useBusinessFeaturesStore()
const marketCountryCode = useMarketplace().market.countryCode

const isAddressCountryPrefillEnabled = !!isFeatureEnabled(
  'addressCountryPrefillEnabled',
)

const isFetchingOrder = ref(false)
const order = ref<GetMerchantOrdersDetails>()
const isSubmitting = ref(false)

const { data: shippers, pending: isLoading } = await useHttpFetchV1(
  getBackshipShippers,
  {
    transform(data) {
      return data.results?.map((shipper) => ({
        label: shipper.shipper_name,
        value: String(shipper.id),
      }))
    },
    default() {
      return []
    },
  },
)

const defaultShipper =
  shippers.value.find(
    (shipper) => shipper.value === String(DEFAULT_BACKSHIP_SHIPPER.id),
  )?.value || ''

function getInitialFormValues(
  deliveryAddress?: GetMerchantOrdersDetails['deliveryAddress'],
  shipper?: {
    label: string
    value: string
  },
) {
  const fullPhoneNumber =
    (deliveryAddress?.countryDialInCode || '') +
    (deliveryAddress?.phoneNumber || '')

  return {
    city: deliveryAddress?.city || '',
    company: deliveryAddress?.company || '',
    country: (deliveryAddress?.country as Country) || '',
    defaultCountry: (isAddressCountryPrefillEnabled
      ? marketCountryCode
      : deliveryAddress?.country) as Country,
    firstName: deliveryAddress?.firstName || '',
    lastName: deliveryAddress?.lastName || '',
    order: order.value?.id?.toString() || '',
    phone: fullPhoneNumber || '',
    postalCode: deliveryAddress?.postalCode || '',
    shipper: shipper?.value || defaultShipper,
    stateOrProvince: deliveryAddress?.stateOrProvince || '',
    street: deliveryAddress?.street || '',
    street2: deliveryAddress?.street2 || '',
  }
}

const initialFormValues = ref<CreateLabelFormValues>(getInitialFormValues())

function validateForm() {
  const requiredError = i18n(translations.required)

  return makeValidate<CreateLabelFormValues>({
    shipper: {
      required: required(requiredError),
    },
    order: {
      required: required(requiredError),
    },
    ...AddressFieldsValidators(i18n),
  })
}

async function handleSubmit(values: CreateLabelFormValues) {
  try {
    isSubmitting.value = true

    await $httpFetch('/bm/shipping/labels', {
      method: 'POST',
      body: {
        order: Number(values.order),
        shipper: Number(values.shipper),
        address: decamelizeKeys(
          values,
        ) as unknown as PostLabelsRequest['address'],
      },
    })

    openSuccessToast({
      content: i18n(translations.successMessage),
      title: i18n(translations.successTitle),
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSubmitting.value = false
  }
}

async function handleSearchOrder(orderId: string) {
  try {
    isFetchingOrder.value = true

    order.value = await $httpFetchV1(getMerchantOrdersDetails, {
      pathParams: {
        orderId,
      },
    })

    initialFormValues.value = getInitialFormValues(
      order.value.deliveryAddress,
      shippers.value.find(
        (shipper) => shipper.value === order.value?.shipping?.carrier?.id,
      ),
    )
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isFetchingOrder.value = false
  }
}
</script>
